import { useSyncExternalStore } from 'react';
import { store } from 'shared/library/use-local-storage';

type HistoryValue = number | string;
type HistorySnapshot = { history: HistoryValue[]; loaded: boolean };

const EMPTY_SNAPSHOT: HistorySnapshot = { history: [], loaded: false };
const listeners = new Set<() => void>();
let snapshot = EMPTY_SNAPSHOT;
let loading: Promise<void> | undefined;
let writes = Promise.resolve();

function valueKey(value: HistoryValue) {
  return typeof value === 'number' ? `breed-${value}` : `query-${value.trim().toLowerCase()}`;
}

function normalizeHistory(value: unknown): HistoryValue[] {
  if (!Array.isArray(value)) return [];

  const seen = new Set<string>();
  const values: HistoryValue[] = [];

  for (const item of value) {
    if (
      (typeof item !== 'number' || !Number.isInteger(item) || item <= 0) &&
      (typeof item !== 'string' || !item.trim())
    ) {
      continue;
    }

    const normalized = typeof item === 'string' ? item.trim() : item;
    const key = valueKey(normalized);
    if (seen.has(key)) continue;
    seen.add(key);
    values.push(normalized);
    if (values.length === 10) break;
  }

  return values;
}

function publish(history: HistoryValue[]) {
  snapshot = { history, loaded: true };
  listeners.forEach((listener) => listener());
}

function loadHistory() {
  loading ??= (async () => {
    try {
      const value = await store.getItem('breed-input');
      publish(normalizeHistory(value ? JSON.parse(value) : []));
    } catch (error) {
      console.warn('Unable to load breed selection history', error);
      publish([]);
    }
  })();

  return loading;
}

function updateHistory(update: (history: HistoryValue[]) => HistoryValue[]) {
  writes = writes.then(async () => {
    await loadHistory();
    const history = normalizeHistory(update(snapshot.history));
    publish(history);

    try {
      await store.setItem('breed-input', JSON.stringify(history));
    } catch (error) {
      console.warn('Unable to save breed selection history', error);
    }
  });
}

function remember(value: HistoryValue) {
  updateHistory((history) => [value, ...history]);
}

function forget(value: HistoryValue) {
  updateHistory((history) => history.filter((item) => valueKey(item) !== valueKey(value)));
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  void loadHistory();
  return () => listeners.delete(listener);
}

export function useBreedSelectionHistory() {
  const current = useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => EMPTY_SNAPSHOT,
  );
  return { ...current, remember, forget };
}
